/**
 * CONNECTOR_VENDOR_MAP — maps evidence connector kinds to vendor catalog
 * slugs with display names and default risk tiers.
 *
 * V89-8-1 / D-89-10: bridges the evidence_connectors and supplier_portfolio
 * data islands so a connector activation can auto-link or prompt the user
 * to add the vendor as a tracked supplier.
 *
 * G-1j (D-CI-30): derived from the connector manifests (`vendorSlug`, the
 * provider's name, `defaultRiskTier` or the family default), so a new kind
 * prompts "track this vendor" with no edit here. Vendors that have no
 * connector kind (ticketing, GRC tools) stay listed by hand below.
 */
import { CONNECTOR_MANIFESTS } from '../connectors/manifests.js';
import { getProvider } from '../connectors/providers.js';
/** Supplier risk tier by connector family (a manifest's `defaultRiskTier` overrides it). */
export const FAMILY_RISK_TIER = {
    idp: 'critical',
    cloud: 'critical',
    scm: 'critical',
    kubernetes: 'critical',
    ai_provider: 'high',
    ai_gateway: 'high',
    vector_db: 'high',
    inference_runtime: 'high',
    ml_registry: 'high',
    saas: 'high',
    vuln: 'high',
    observability: 'medium',
    siem: 'high',
    ingest: 'low',
};
/** Vendors a customer may track that no connector kind observes. */
const VENDORS_WITHOUT_KIND = [
    { connector_kind: 'jira', vendor_slug: 'jira', display_name: 'Jira (Atlassian)', default_risk_tier: 'high' },
    { connector_kind: 'servicenow', vendor_slug: 'servicenow', display_name: 'ServiceNow', default_risk_tier: 'high' },
    { connector_kind: 'vanta', vendor_slug: 'vanta', display_name: 'Vanta', default_risk_tier: 'medium' },
    { connector_kind: 'drata', vendor_slug: 'drata', display_name: 'Drata', default_risk_tier: 'medium' },
];
function fromManifests() {
    return CONNECTOR_MANIFESTS.filter((m) => m.vendorSlug && m.family !== 'ingest').map((m) => ({
        connector_kind: m.kind,
        vendor_slug: m.vendorSlug,
        // The vendor's name (the provider registry), not the connection label ("Okta · AI app discovery").
        display_name: getProvider(m.provider.key)?.name ?? m.ui.label,
        default_risk_tier: m.defaultRiskTier ?? FAMILY_RISK_TIER[m.family],
    }));
}
export const CONNECTOR_VENDOR_MAP = Object.freeze([...fromManifests(), ...VENDORS_WITHOUT_KIND]);
export function getVendorForConnector(connectorKind) {
    return fromManifests().find((e) => e.connector_kind === connectorKind) ?? VENDORS_WITHOUT_KIND.find((e) => e.connector_kind === connectorKind);
}
//# sourceMappingURL=connector-vendor-map.js.map